import { useEffect, useId, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { clientsAPI } from '../utils/api';
import type { Cliente } from '../types';

export function clientLabel(client: Cliente): string {
  return client.ragione_sociale?.trim() || [client.nome, client.cognome].filter(Boolean).join(' ').trim() || 'Cliente senza nome';
}

interface Props {
  value: string;
  selectedClient?: Cliente;
  onChange: (client: Cliente) => void;
  disabled?: boolean;
}

export default function ClientPicker({ value, selectedClient, onChange, disabled }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [request, setRequest] = useState({ search: '', page: 1 });
  const [picked, setPicked] = useState<Cliente>();
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const panelId = useId();
  useEffect(() => {
    if (search.trim() === request.search) return;
    const timer = window.setTimeout(() => setRequest({ search: search.trim(), page: 1 }), 300);
    return () => window.clearTimeout(timer);
  }, [search, request.search]);
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: ['clients', 'offer-picker', request],
    queryFn: async () => (await clientsAPI.getClients({ ...request, limit: 20 })).data,
    enabled: open && !disabled,
  });
  const waiting = isFetching || search.trim() !== request.search;
  const selected = selectedClient?.id === value ? selectedClient : picked?.id === value ? picked : undefined;
  const buttonClass = 'rounded border border-gray-300 px-3 py-1.5 text-sm disabled:opacity-50';
  return (
    <div ref={root} className="relative" onKeyDown={(event) => {
      if (event.key === 'Escape' && open) {
        event.preventDefault(); event.stopPropagation(); setOpen(false); trigger.current?.focus();
      }
    }}>
      <button id="offer-client" ref={trigger} type="button" aria-expanded={open && !disabled} aria-controls={panelId}
        disabled={disabled} onClick={() => setOpen(!open)}
        className="flex w-full items-center justify-between rounded-md border border-gray-300 px-3 py-1.5 text-left text-sm focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500">
        <span>{selected ? clientLabel(selected) : value ? 'Caricamento cliente…' : 'Seleziona un cliente'}</span><span aria-hidden="true">▾</span>
      </button>
      {open && !disabled && (
        <div id={panelId} className="absolute z-30 mt-1 w-full rounded-md border border-gray-200 bg-white p-2 shadow-lg">
          <input autoFocus aria-label="Cerca cliente" placeholder="Cerca ragione sociale, nome, email o P. IVA"
            value={search} onChange={(event) => setSearch(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') event.preventDefault(); }}
            className="w-full rounded border border-gray-300 px-3 py-2 text-sm" />
          <div aria-live="polite" className="max-h-60 overflow-y-auto py-1">
            {waiting ? <p className="p-2 text-sm text-gray-500">Caricamento…</p> : isError ? (
              <div role="alert" className="p-2 text-sm">Impossibile caricare i clienti. <button type="button" onClick={() => refetch()} className="underline">Riprova</button></div>
            ) : data?.data.length ? data.data.map((client) => (
              <button key={client.id} type="button" aria-pressed={value === client.id}
                className="block w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 aria-pressed:bg-primary-50"
                onClick={() => { setPicked(client); onChange(client); setOpen(false); trigger.current?.focus(); }}>
                {clientLabel(client)}
              </button>
            )) : <p className="p-2 text-sm text-gray-500">Nessun cliente trovato</p>}
          </div>
          <div className="flex items-center justify-between gap-2 border-t pt-2">
            <button type="button" className={buttonClass} disabled={waiting || request.page <= 1} onClick={() => setRequest({ ...request, page: request.page - 1 })}>Precedente</button>
            <span className="text-xs text-gray-500">Pagina {request.page} di {Math.max(1, data?.pagination.pages || 1)}</span>
            <button type="button" className={buttonClass} disabled={waiting || isError || !data || request.page >= data.pagination.pages} onClick={() => setRequest({ ...request, page: request.page + 1 })}>Successiva</button>
          </div>
        </div>
      )}
    </div>
  );
}
